// Theme preset configurations
export interface ThemePreset {
  name: string
  displayName: string
  colors: {
    primary: string
    primaryHover: string
    primaryLight: string
    primaryDark: string
    background: string
    backgroundLight: string
    backgroundDark: string
    surface: string
    accent: string
    text: string
    textSecondary: string
    textMuted: string
    border: string
    borderLight: string
    borderHover: string
  }
}

export const themePresets: Record<string, ThemePreset> = {
  'aifm-brand': {
    name: 'aifm-brand',
    displayName: 'AIFM Brand',
    colors: {
      primary: '#00f2ff',                        // Bright Cyan (DESIGN_SYSTEM.md accent)
      primaryHover: '#00d4e0',                   // Slightly darker cyan
      primaryLight: 'rgba(0, 242, 255, 0.6)',    // Light cyan with alpha
      primaryDark: '#00a8b3',                    // Dark cyan
      background: '#080a0f',                     // Deep blue-black (NOT pure black)
      backgroundLight: 'rgba(255, 255, 255, 0.03)',  // Cards, elevated surfaces
      backgroundDark: '#050709',                 // Darker variant
      surface: 'rgba(15, 18, 25, 0.8)',          // Alternative card bg
      accent: '#00f2ff',                         // Primary accent (cyan)
      text: '#f8fafc',                           // Main text (not pure white)
      textSecondary: '#94a3b8',                  // Muted text, labels
      textMuted: 'rgba(255, 255, 255, 0.5)',     // Disabled, hints
      border: 'rgba(255, 255, 255, 0.08)',       // Card borders
      borderLight: 'rgba(255, 255, 255, 0.05)', // Subtle dividers
      borderHover: 'rgba(0, 242, 255, 0.3)'      // Hover emphasis
    }
  },
  'black-silver': {
    name: 'black-silver',
    displayName: 'Black & Silver',
    colors: {
      primary: '#9CA3AF',        // Silver gray
      primaryHover: '#6B7280',   // Darker silver
      primaryLight: '#D1D5DB',   // Light silver
      primaryDark: '#4B5563',    // Dark silver
      background: '#080a0f',     // Deep blue-black
      backgroundLight: 'rgba(255, 255, 255, 0.03)',
      backgroundDark: '#050709',
      surface: 'rgba(15, 18, 25, 0.8)',
      accent: '#9CA3AF',         // Silver accent
      text: '#f8fafc',
      textSecondary: '#94a3b8',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      border: 'rgba(255, 255, 255, 0.08)',
      borderLight: 'rgba(255, 255, 255, 0.05)',
      borderHover: 'rgba(156, 163, 175, 0.3)'
    }
  },
  'gold-black': {
    name: 'gold-black',
    displayName: 'Gold & Black',
    colors: {
      primary: '#D4AF37',        // Gold
      primaryHover: '#B8952F',   // Darker gold
      primaryLight: '#E5C763',   // Light gold
      primaryDark: '#A08429',    // Dark gold
      background: '#080a0f',     // Deep blue-black
      backgroundLight: 'rgba(255, 255, 255, 0.03)',
      backgroundDark: '#050709',
      surface: 'rgba(15, 18, 25, 0.8)',
      accent: '#B8952F',         // Gold accent
      text: '#f8fafc',
      textSecondary: '#94a3b8',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      border: 'rgba(255, 255, 255, 0.08)',
      borderLight: 'rgba(255, 255, 255, 0.05)',
      borderHover: 'rgba(212, 175, 55, 0.3)'
    }
  },
  'gold-white': {
    name: 'gold-white',
    displayName: 'Gold & White',
    colors: {
      primary: '#D4AF37',        // Gold
      primaryHover: '#B8952F',   // Darker gold
      primaryLight: '#E5C763',   // Light gold
      primaryDark: '#A08429',    // Dark gold
      background: '#ffffff',     // White
      backgroundLight: '#f9f9f9',
      backgroundDark: '#f0f0f0',
      surface: 'rgba(0, 0, 0, 0.03)',
      accent: '#B8952F',         // Gold accent
      text: '#1a1a1a',           // Dark text for white background
      textSecondary: 'rgba(26, 26, 26, 0.7)',
      textMuted: 'rgba(26, 26, 26, 0.5)',
      border: 'rgba(212, 175, 55, 0.15)',
      borderLight: 'rgba(212, 175, 55, 0.1)',
      borderHover: 'rgba(212, 175, 55, 0.3)'
    }
  },
  'blue-gray': {
    name: 'blue-gray',
    displayName: 'Blue & Gray',
    colors: {
      primary: '#3B82F6',        // Blue
      primaryHover: '#2563EB',   // Darker blue
      primaryLight: '#60A5FA',   // Light blue
      primaryDark: '#1D4ED8',    // Dark blue
      background: '#1F2937',     // Gray
      backgroundLight: '#374151',
      backgroundDark: '#111827',
      surface: 'rgba(31, 41, 55, 0.8)',
      accent: '#2563EB',         // Blue accent
      text: '#f8fafc',
      textSecondary: '#94a3b8',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      border: 'rgba(255, 255, 255, 0.08)',
      borderLight: 'rgba(255, 255, 255, 0.05)',
      borderHover: 'rgba(59, 130, 246, 0.3)'
    }
  },
  'green-dark': {
    name: 'green-dark',
    displayName: 'Green & Dark',
    colors: {
      primary: '#10B981',        // Green
      primaryHover: '#059669',   // Darker green
      primaryLight: '#34D399',   // Light green
      primaryDark: '#047857',    // Dark green
      background: '#080a0f',     // Deep blue-black
      backgroundLight: 'rgba(255, 255, 255, 0.03)',
      backgroundDark: '#050709',
      surface: 'rgba(15, 18, 25, 0.8)',
      accent: '#059669',         // Green accent
      text: '#f8fafc',
      textSecondary: '#94a3b8',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      border: 'rgba(255, 255, 255, 0.08)',
      borderLight: 'rgba(255, 255, 255, 0.05)',
      borderHover: 'rgba(16, 185, 129, 0.3)'
    }
  },
  'purple-dark': {
    name: 'purple-dark',
    displayName: 'Purple & Dark',
    colors: {
      primary: '#8B5CF6',        // Purple
      primaryHover: '#7C3AED',   // Darker purple
      primaryLight: '#A78BFA',   // Light purple
      primaryDark: '#6D28D9',    // Dark purple
      background: '#080a0f',     // Deep blue-black
      backgroundLight: 'rgba(255, 255, 255, 0.03)',
      backgroundDark: '#050709',
      surface: 'rgba(15, 18, 25, 0.8)',
      accent: '#7C3AED',         // Purple accent
      text: '#f8fafc',
      textSecondary: '#94a3b8',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      border: 'rgba(255, 255, 255, 0.08)',
      borderLight: 'rgba(255, 255, 255, 0.05)',
      borderHover: 'rgba(139, 92, 246, 0.3)'
    }
  },
  'atn-teal': {
    name: 'atn-teal',
    displayName: 'ATN Teal & Black',
    colors: {
      primary: '#14B8A6',        // Teal-500
      primaryHover: '#0D9488',   // Teal-600
      primaryLight: '#5EEAD4',   // Teal-300
      primaryDark: '#0F766E',    // Teal-700
      background: '#080a0f',     // Deep blue-black
      backgroundLight: 'rgba(255, 255, 255, 0.03)',
      backgroundDark: '#050709',
      surface: 'rgba(15, 18, 25, 0.8)',
      accent: '#14B8A6',         // Teal accent
      text: '#f8fafc',
      textSecondary: '#94a3b8',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      border: 'rgba(255, 255, 255, 0.08)',
      borderLight: 'rgba(255, 255, 255, 0.05)',
      borderHover: 'rgba(20, 184, 166, 0.3)'
    }
  },
  // Keep atn-red as alias for backwards compatibility
  'atn-red': {
    name: 'atn-teal',
    displayName: 'ATN Teal & Black',
    colors: {
      primary: '#14B8A6',        // Teal-500
      primaryHover: '#0D9488',   // Teal-600
      primaryLight: '#5EEAD4',   // Teal-300
      primaryDark: '#0F766E',    // Teal-700
      background: '#080a0f',     // Deep blue-black
      backgroundLight: 'rgba(255, 255, 255, 0.03)',
      backgroundDark: '#050709',
      surface: 'rgba(15, 18, 25, 0.8)',
      accent: '#14B8A6',         // Teal accent
      text: '#f8fafc',
      textSecondary: '#94a3b8',
      textMuted: 'rgba(255, 255, 255, 0.5)',
      border: 'rgba(255, 255, 255, 0.08)',
      borderLight: 'rgba(255, 255, 255, 0.05)',
      borderHover: 'rgba(20, 184, 166, 0.3)'
    }
  }
}

// Helper function to detect theme preset from colors
export function detectThemePreset(primaryColor: string, backgroundColor: string): string | null {
  // Normalize colors to uppercase for comparison
  const normalizedPrimary = primaryColor?.toUpperCase()
  const normalizedBg = backgroundColor?.toUpperCase()
  
  // Check each preset
  for (const [key, preset] of Object.entries(themePresets)) {
    const presetPrimary = preset.colors.primary.toUpperCase()
    const presetBg = preset.colors.background.toUpperCase()
    
    // Check if colors match (with some flexibility for similar colors)
    if (
      (normalizedPrimary === presetPrimary || 
       (normalizedPrimary?.includes('D4AF37') && presetPrimary.includes('D4AF37')) ||
       (normalizedPrimary?.includes('9CA3AF') && presetPrimary.includes('9CA3AF'))) &&
      (normalizedBg === presetBg || 
       (normalizedBg?.includes('000000') && presetBg.includes('000000')) ||
       (normalizedBg?.includes('FFFFFF') && presetBg.includes('FFFFFF')))
    ) {
      return key
    }
  }
  
  // Fallback detection based on color characteristics
  if (normalizedPrimary?.includes('14B8A6') || normalizedPrimary?.includes('0D9488')) {
    return 'atn-teal'
  }
  // Legacy support for old red colors - redirect to teal
  if (normalizedPrimary?.includes('DC5B65') || normalizedPrimary?.includes('C94B55')) {
    return 'atn-teal'
  }
  if (normalizedPrimary?.includes('9CA3AF') || normalizedPrimary?.includes('6B7280')) {
    return 'black-silver'
  }
  if (normalizedPrimary?.includes('D4AF37') && normalizedBg?.includes('FFF')) {
    return 'gold-white'
  }
  if (normalizedPrimary?.includes('D4AF37')) {
    return 'gold-black'
  }
  
  return null
}

// Get theme preset or default
export function getThemePreset(presetName: string | null): ThemePreset {
  if (presetName && themePresets[presetName]) {
    return themePresets[presetName]
  }
  // Default to black-silver if no preset found
  return themePresets['black-silver']
}